import { useState, useEffect } from 'react';
import { Eye, EyeOff, HelpCircle } from 'lucide-react';
import { providersByType, configFieldsByProvider } from '../providerConfig';
import type { StepProps } from '../types';

export function ConfigStep({ data, onChange, onValidChange }: StepProps) {
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  const providerInfo = providersByType[data.type].find(p => p.provider === data.provider);
  const fields = configFieldsByProvider[data.provider] || [];

  useEffect(() => {
    const isValid = validateForm();
    onValidChange(isValid);
  }, [data.name, data.config]);

  const validateForm = () => {
    if (!data.name.trim()) return false;

    for (const field of fields) {
      if (field.required) {
        const value = data.config[field.key];
        if (value === undefined || value === '') return false;
      }
    }

    return true;
  };

  const handleNameChange = (name: string) => {
    onChange({ name });
  };

  const handlePriorityChange = (priority: number) => {
    onChange({ priority });
  };

  const handleConfigChange = (key: string, value: string | number | boolean) => {
    onChange({
      config: { ...data.config, [key]: value },
    });
  };

  const toggleSecretVisibility = (key: string) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Configura {providerInfo?.name}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Ingresa las credenciales y configuración necesarias
        </p>
      </div>

      {/* Basic Info */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre de la integración <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder={`${providerInfo?.name} Principal`}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-brand-500 focus:border-transparent
                     placeholder:text-gray-400 dark:placeholder:text-gray-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Un nombre descriptivo para identificar esta integración
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Prioridad
            <button
              type="button"
              className="ml-2 text-gray-400 hover:text-gray-600"
              title="0 = Primario (se usa primero), 1+ = Fallback (se usa si anteriores fallan)"
            >
              <HelpCircle className="w-4 h-4 inline" />
            </button>
          </label>
          <input
            type="number"
            min="0"
            value={data.priority}
            onChange={(e) => handlePriorityChange(parseInt(e.target.value) || 0)}
            className="w-28 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            0 = Primario, 1+ = Fallback
          </p>
        </div>
      </div>

      {/* Provider Config Fields */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-white mb-4">
          Credenciales de {providerInfo?.name}
        </h4>

        <div className="space-y-4">
          {fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
                {field.helpText && (
                  <button
                    type="button"
                    className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title={field.helpText}
                  >
                    <HelpCircle className="w-4 h-4 inline" />
                  </button>
                )}
              </label>

              {field.type === 'boolean' ? (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!data.config[field.key]}
                    onChange={(e) => handleConfigChange(field.key, e.target.checked)}
                    className="w-5 h-5 text-brand-500 rounded border-gray-300 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Habilitar {field.label}
                  </span>
                </label>
              ) : (
                <div className="relative">
                  <input
                    type={field.type === 'password' && !showSecrets[field.key] ? 'password' : field.type === 'number' ? 'number' : 'text'}
                    value={(data.config[field.key] as string) ?? ''}
                    onChange={(e) => handleConfigChange(
                      field.key,
                      field.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value
                    )}
                    placeholder={field.placeholder}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-brand-500 focus:border-transparent
                             placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                  {field.type === 'password' && (
                    <button
                      type="button"
                      onClick={() => toggleSecretVisibility(field.key)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                    >
                      {showSecrets[field.key] ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
